import { css } from '@linaria/atomic'
import { getThemeSpacing } from '../styles/appTheme'
import { palette } from '../styles/colors'
import shape from '../styles/shape'
import { APP_FONT } from '../theme'

export const fieldStyles = css`
    display: inline-flex;
    flex-direction: column;
    vertical-align: top;
    min-width: 0;

    & .field-control {
        position: relative;
        display: flex;
        align-items: center;
        border-radius: ${shape.borderRadius}px;
        color: ${palette.contrastText};
    }

    & .field-input {
        flex: 1;
        min-width: 0;
        box-sizing: border-box;
        display: block;
        font-family: ${APP_FONT};
        font-size: 14px;
        font-weight: 500;
        line-height: 1.4375em;
        color: currentcolor;
        background: none;
        border: 0;
        margin: 0;
        outline: none;
        padding-top: 16.5px;
        padding-bottom: 16.5px;
        padding-left: 14px;
        padding-right: 14px;
        caret-color: ${palette.greenMFC};
        -webkit-tap-highlight-color: transparent;
    }

    & .field-input::placeholder {
        color: ${palette.dimContrastIcons};
        opacity: 1;
    }

    & .field-input:disabled {
        color: ${palette.disabledText};
        -webkit-text-fill-color: ${palette.disabledText};
    }

    /* Chrome/Safari force color and background-color on autofilled inputs from
     * the UA sheet with !important, so the currentcolor above loses (and would
     * resolve to the forced color, so it can't be used here either). A
     * transition outranks even !important UA declarations, so delaying both
     * forever keeps the field transparent on translucent surfaces. Clipping the
     * background to the text instead left Chrome painting the glyphs in the
     * forced color until something repainted the field (e.g. hovering it). */
    & .field-input:-webkit-autofill {
        -webkit-text-fill-color: ${palette.contrastText};
        transition-property: background-color, color;
        transition-delay: 600000s;
    }

    & textarea.field-input {
        resize: none;
    }

    & .field-outline {
        position: absolute;
        inset: -5px 0 0 0;
        margin: 0;
        padding: 0 8px;
        min-width: 0%;
        overflow: hidden;
        pointer-events: none;
        text-align: left;
        border-radius: inherit;
        border-width: 1px;
        border-style: solid;
        border-color: ${palette.defaultInputBorder};
    }

    & .field-outline legend {
        float: unset;
        display: block;
        width: auto;
        height: 11px;
        padding: 0;
        font-size: 0.75em;
        white-space: nowrap;
        visibility: hidden;
        max-width: 0.01px;
        transition: max-width 50ms cubic-bezier(0, 0, 0.2, 1);
    }

    & .field-outline legend > span {
        display: inline-block;
        padding-left: 5px;
        padding-right: 5px;
        opacity: 0;
        visibility: visible;
        /* mirrors .field-label so the notch it reserves is the right width */
        text-transform: capitalize;
    }

    &:hover .field-outline {
        border-color: ${palette.contrastText};
    }

    & .field-input:focus ~ .field-outline {
        border-width: 2px;
        border-color: ${palette.greenMFC};
    }

    & .field-input:disabled ~ .field-outline {
        border-color: ${palette.disabledText};
    }

    & .field-helper {
        font-family: ${APP_FONT};
        font-size: 0.75rem;
        font-weight: 400;
        line-height: 1.66;
        color: ${palette.drawerLabel};
        margin-top: 3px;
        margin-bottom: 0;
        margin-left: 14px;
        margin-right: 14px;
    }
`

/* The label at rest, sitting over the input. TextField floats it; Select pins it
 * shrunk with fieldShrunkLabelStyles, because a select is never empty. */
export const fieldLabelStyles = css`
    & .field-label {
        position: absolute;
        top: 0;
        left: 0;
        max-width: calc(100% - 24px);
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        pointer-events: none;
        text-transform: capitalize;
        transform: translate(14px, 16px) scale(1);
        transform-origin: top left;
        font-family: ${APP_FONT};
        font-size: 14px;
        font-weight: 500;
        line-height: 1.4375em;
        color: ${palette.dimContrastIcons};
        transition:
            transform 200ms cubic-bezier(0, 0, 0.2, 1),
            max-width 200ms cubic-bezier(0, 0, 0.2, 1),
            color 200ms cubic-bezier(0, 0, 0.2, 1);
    }

    & .field-input:disabled ~ .field-label {
        color: ${palette.disabledText};
    }
`

export const fieldShrunkLabelStyles = css`
    & .field-label {
        max-width: calc(133% - 32px);
        transform: translate(14px, -9px) scale(0.75);
    }

    & .field-outline legend {
        max-width: 100%;
    }
`

export const fieldSmallStyles = css`
    & .field-input {
        padding-top: 8.5px;
        padding-bottom: 8.5px;
    }

    & .field-label {
        transform: translate(14px, 8px) scale(1);
    }
`

/* every selector here has to match a base one exactly, or cx can't dedupe it */
export const fieldErrorStyles = css`
    & .field-outline {
        border-color: ${palette.mfcWarning};
    }

    &:hover .field-outline {
        border-color: ${palette.mfcWarning};
    }

    & .field-input:focus ~ .field-outline {
        border-color: ${palette.mfcWarning};
    }

    & .field-label {
        color: ${palette.mfcWarning};
    }

    & .field-input:focus ~ .field-label {
        color: ${palette.mfcWarning};
    }

    & .field-helper {
        color: ${palette.mfcWarning};
    }
`

export const fieldFullWidthStyles = css`
    width: 100%;
`

/* Select chrome, shared by the native `Select` and by `PopoverSelect`, whose
 * display is a `<button>` precisely so `:disabled` and `:focus` keep driving
 * the label and outline rules above. */
export const selectStyles = css`
    & .field-input {
        -webkit-appearance: none;
        appearance: none;
        cursor: pointer;
        text-overflow: ellipsis;
        padding-right: 32px;
    }

    & .field-input:disabled {
        cursor: default;
    }

    & .field-chevron {
        position: absolute;
        right: 7px;
        pointer-events: none;
        color: ${palette.contrastText};
    }

    & .field-input:disabled ~ .field-chevron {
        color: ${palette.disabledText};
    }
`

/* A select needs none of the headroom a TextField reserves for a floating label,
 * so it drops to 8 + 8 + 20 — exactly COMPONENT_HEIGHTS.inputSearch, since these
 * sit beside an InputSearch often enough that the two have to agree. */
export const selectBoxedStyles = css`
    & .field-input {
        line-height: 20px;
        padding-top: ${getThemeSpacing(1)};
        padding-bottom: ${getThemeSpacing(1)};
    }
`

export const selectUnderlineStyles = css`
    & .field-control {
        border-radius: 0;
        border-bottom-width: 1px;
        border-bottom-style: solid;
        border-bottom-color: ${palette.defaultInputBorder};
    }

    &:hover .field-control {
        border-bottom-color: ${palette.contrastText};
    }

    &:focus-within .field-control {
        border-bottom-color: ${palette.greenMFC};
    }

    & .field-input {
        padding-top: 4px;
        padding-bottom: 5px;
        padding-left: 0;
        padding-right: 24px;
    }

    & .field-chevron {
        right: 0;
    }
`

/* the underline label sits above the control rather than in a notch, and the
 * label anchors to .field-control, so it has to clear that margin itself */
export const selectUnderlineLabelledStyles = css`
    & .field-control {
        margin-top: 16px;
    }

    & .field-label {
        max-width: 133%;
        transform: translate(0, -17.5px) scale(0.75);
    }
`

export const fieldSpacedStyles = css`
    margin-bottom: ${getThemeSpacing(1)};
`
